import {
  useMemo,
  useState,
  type FocusEvent,
  type InputHTMLAttributes,
} from "react";
import { twMerge } from "tailwind-merge";

type InputWithDropdownProps = {
  readonly value: string;
  readonly options: readonly string[];
  readonly placeholder?: string;
  readonly title?: string;
  readonly disabled?: boolean;
  readonly "aria-label"?: string;
  readonly className?: string;
  readonly onChange: (value: string) => void;
  readonly onBlur?: () => void;
} & Pick<
  InputHTMLAttributes<HTMLInputElement>,
  "inputMode" | "maxLength" | "pattern" | "type"
>;

export const InputWithDropdown = ({
  value,
  options,
  placeholder,
  title,
  disabled = false,
  "aria-label": ariaLabel,
  className,
  onChange,
  onBlur,
  inputMode,
  maxLength,
  pattern,
  type = "text",
}: InputWithDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const filteredOptions = useMemo(() => {
    const normalizedValue = value.trim().toLowerCase();
    if (!normalizedValue) {
      return options;
    }
    return options.filter((option) =>
      option.toLowerCase().includes(normalizedValue),
    );
  }, [options, value]);

  const shouldShowDropdown = isOpen && !disabled && filteredOptions.length > 0;

  const handleBlur = (event: FocusEvent<HTMLDivElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setIsOpen(false);
      onBlur?.();
    }
  };

  return (
    <div className="relative min-w-0" onBlur={handleBlur}>
      <input
        type={type}
        value={value}
        title={title}
        inputMode={inputMode}
        maxLength={maxLength}
        pattern={pattern}
        aria-label={ariaLabel}
        aria-autocomplete="list"
        aria-expanded={shouldShowDropdown}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(event) => {
          onChange(event.target.value);
          setIsOpen(true);
        }}
        onFocus={() => setIsOpen(true)}
        className={twMerge(
          "w-full rounded-lg border border-[var(--border)] bg-input-background px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-70",
          className,
        )}
      />
      {shouldShowDropdown && (
        <div
          className="absolute z-50 mt-1 max-h-44 w-full overflow-y-auto rounded-lg border border-[var(--border)] bg-white py-1 shadow-lg"
          role="listbox"
        >
          {filteredOptions.map((option) => (
            <button
              key={option}
              type="button"
              className="block w-full px-3 py-2 text-left text-sm text-foreground hover:bg-muted focus:bg-muted focus:outline-none"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => {
                onChange(option);
                setIsOpen(false);
              }}
              role="option"
              aria-selected={option === value}
            >
              {option}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
